/* @flow */
import React, { PureComponent } from "react";
import cn from "classnames";
import OutsideClickBoundary from "@raise/outside-click-boundary";
import { CartContext } from "@raise/with-cart-context-provider";
import type { Card } from "@raise/with-cart-context-provider";
import type { Node } from "react";
import Loader from "../../Loader";

import s from "./styles.scss";
import { calculateTimerColor, toMoney } from "./Util";
import TimerHeader from "./TimerHeader";
import ListItem from "./ListItem";

type RemoveProps = {
  brand_uuid: string,
  listing_uuid: string,
  user_uuid: string,
  isUnifiedCart: boolean,
};

export type CartMenuInternalProps = {
  className?: string,
  cards: Array<Card>,
  isOpen: boolean,
  dataset?: { [string]: any },
  onClick: (MouseEvent) => void,
  // onRemoveCard: (id: string, isAvailable: boolean) => void,
  onRemoveCard: (RemoveProps) => void,
  isCartLoading: boolean,
  totalPrice: string,
  children: Node,
  time: {
    m: number,
    s: number,
  },
  seconds: number,
  timeIsUp: boolean,
  userUuid: string,
  savings: string,
};

const cartMenuDefaultProps = {
  className: "",
  cards: [],
  isOpen: false,
  dataset: {},
  onClick: () => {},
  onRemoveCard: () => {},
  isCartLoading: false,
  totalPrice: "$0.00",
};

/**
 * @namespace CartMenu
 */

export class CartMenuInternal extends PureComponent<CartMenuInternalProps> {
  static defaultProps = cartMenuDefaultProps;

  goToCart() {
    window.location.assign("/cart");
  }

  removeCardHandler: (Card) => void;
  removeCardHandler = (card: Card) => {
    this.props.onRemoveCard({
      brand_uuid: card.brandUuid,
      listing_uuid: card.listingUuid,
      user_uuid: this.props.userUuid,
      lineItemId: undefined, // TODO
      isUnifiedCart: true,
    });
  };

  render() {
    const {
      isCartLoading,
      className,
      children,
      dataset,
      onClick,
      isOpen,
      cards,
      totalPrice,
      timeIsUp,
      seconds,
      savings,
    } = this.props;

    const menu = (
      <div className={s.content}>
        <TimerHeader numberOfCards={cards.length} seconds={seconds} />
        <ul className={s.list}>
          {cards.map((item) => (
            <ListItem
              key={item.uuid}
              card={item}
              removeCard={this.removeCardHandler}
            />
          ))}
        </ul>
        <div className={s.statusArea}>
          {isCartLoading && (
            <div className={s.loadingOverlay}>
              <Loader isLoading={true} />
            </div>
          )}
          <div className={s.subtotal}>
            <div className={s.row}>
              Subtotal
              <strong>{toMoney(totalPrice)}</strong>
            </div>
            <div className={s.bottomRow}>
              Your Savings
              <span>{savings}</span>
            </div>
          </div>
        </div>

        <button
          isDisabled={isCartLoading}
          data-testid="review-cart-button"
          data-logid="review-and-checkout-cart-menu-button"
          data-gtm="true"
          onClick={isCartLoading ? () => {} : this.goToCart}
          className={s.checkoutButton}
        >
          REVIEW AND CHECKOUT
        </button>
        <div className={s.guarantee}>
          <img
            alt=""
            className={s.guaranteeIcon}
            src="/assets/guarantee_shield.svg"
          />
          90 Day Money-Back Guarantee
        </div>
      </div>
    );

    return (
      <OutsideClickBoundary
        className={s.cartMenu}
        onClick={isOpen ? onClick : () => {}}
        {...dataset}
      >
        <div className={cn(s.cartMenu, className)}>
          <button
            data-testid="cart-menu-button"
            data-logid="cart-menu-button"
            className={s.unsetButton}
            onClick={onClick}
          >
            {cards.length > 0 && (
              <span
                className={cn(s.itemCount, {
                  [s.warningBackground]: timeIsUp,
                })}
                style={{
                  ...(!timeIsUp
                    ? { backgroundColor: calculateTimerColor(seconds) }
                    : {}),
                }}
              >
                {(!timeIsUp && cards.length) || "!"}
              </span>
            )}
            <div className={cn(s.cart)}>{children}</div>
          </button>
          <div
            className={cn(s.popover, {
              [s.popoverClosed]: !isOpen,
            })}
          >
            <div className={s.dropdown}>
              {timeIsUp ? (
                <span className={cn(s.singleText, s.expired)}>
                  Your cart has expired.
                </span>
              ) : cards.length > 0 ? (
                menu
              ) : (
                <span className={s.singleText}>Your cart is empty.</span>
              )}
            </div>
          </div>
        </div>
      </OutsideClickBoundary>
    );
  }
}

export type CartMenuProps = {
  className?: string,
  dataset?: { [string]: any },
  children: Node,
  userUuid: string,
};

const cartMenuInternalDefaultProps = {
  className: "",
  dataset: {},
};

const CartMenu = (props: CartMenuProps) => (
  <CartContext.Consumer>
    {({
      cards,
      isCartOpen,
      toggleCart,
      removeCard,
      totalPrice,
      isCartLoading,
      time,
      seconds,
      timeIsUp,
      userUuid,
      savings,
    }) => (
      <CartMenuInternal
        isOpen={isCartOpen}
        onClick={toggleCart}
        cards={cards}
        onRemoveCard={removeCard}
        totalPrice={totalPrice}
        isCartLoading={isCartLoading}
        time={time}
        seconds={seconds}
        timeIsUp={timeIsUp}
        userUuid={userUuid}
        savings={savings}
        {...props}
      />
    )}
  </CartContext.Consumer>
);

CartMenu.defaultProps = cartMenuInternalDefaultProps;

export default CartMenu;
